import { BLOCKS } from '@contentful/rich-text-types';
import type { DocumentWithEmbeddedEntries } from '@/libs/contentful/types';
import { extractTextFromNode, getIdFromText } from '@/utils/formatters';

/**
 * Matches paragraphs whose entire content is wrapped in `[toc]...[/toc]`
 * (e.g. `"[toc]Travel Perks[/toc]"`), promoting them to sub-section headings.
 * The inner text may be empty (`[toc][/toc]`) for untitled sub-sections —
 * those still get a letter label in the body but are skipped by the TOC.
 * Group 1 is the heading text inside the wrap.
 */
export const TOC_HEADING_RE = /^\s*\[toc\]\s*(.*?)\s*\[\/toc\]\s*$/;

/** Strips any opening or closing `[toc]` marker, along with adjacent whitespace, from a text segment. */
export const TOC_MARKER_RE = /\s*\[\/?toc\]\s*/g;

/**
 * Matches a block (paragraph or heading) whose entire text content is wrapped in
 * `[center]...[/center]` (e.g. `"[center]KEEP THIS DOCUMENT FOR FUTURE USE[/center]"`).
 * The Contentful rich-text editor has no native block-alignment control, so
 * editors add these markers to center a block; the renderer strips them and
 * applies centering. Group 1 is the inner content.
 */
export const CENTER_BLOCK_RE = /^\s*\[center\]([\s\S]*)\[\/center\]\s*$/;

/** Strips any opening or closing `[center]` marker, along with adjacent whitespace, from a text segment. */
export const CENTER_MARKER_RE = /\s*\[\/?center\]\s*/g;

/**
 * Matches a paragraph whose entire content is a `[below-rev]` marker.
 * Fine print content after the marker renders below the fine print block and
 * the (optional) "Rev." date — migrated agreement pages use this for
 * "Important Information" boxes that WP disclaimer scripts injected there.
 */
export const BELOW_REV_PARAGRAPH_RE = /^\s*\[below-rev\]\s*$/;

/** Strips any `[below-rev]` marker, along with adjacent whitespace, from a text segment. */
export const BELOW_REV_MARKER_RE = /\s*\[below-rev\]\s*/g;

/**
 * Splits a finePrint document at the first `[below-rev]` marker
 * paragraph. Everything before the marker renders in the usual fine print
 * spot; everything after renders below it and the (optional) "Rev." date.
 * The marker paragraph itself is dropped. Without a marker, the whole
 * document is `main`.
 */
/** Fine print split around its marker: what renders inline, and what renders below the page. */
interface SplitFinePrint {
  main?: DocumentWithEmbeddedEntries;
  below?: DocumentWithEmbeddedEntries;
}

export function splitFinePrintAtMarker(finePrint?: DocumentWithEmbeddedEntries): SplitFinePrint {
  const content = finePrint?.json?.content;
  if (!content) return { main: finePrint };

  const markerIndex = content.findIndex(
    node => node.nodeType === BLOCKS.PARAGRAPH && BELOW_REV_PARAGRAPH_RE.test(extractTextFromNode(node)),
  );
  if (markerIndex === -1) return { main: finePrint };

  const main = content.slice(0, markerIndex);
  const below = content.slice(markerIndex + 1);
  return {
    main: main.length > 0 ? { ...finePrint, json: { ...finePrint.json, content: main } } : undefined,
    below: below.length > 0 ? { ...finePrint, json: { ...finePrint.json, content: below } } : undefined,
  };
}

// `pageAgreement.tableLayout` value that switches the tables to the GLBA
// model-form layout (shaded label column / header row / section bands, model-form
// column widths, closing dividers). Any other value — or unset — renders the
// default agreement-table styling. Editors opt in per page in Contentful, so the
// shared AgreementTable renderer leaves every other agreement page unchanged.
export const isGlbaModelForm = (tableLayout?: string): boolean => tableLayout === 'GLBA Model Form';

export function getTocAnchor(sectionAnchor: string, headingText: string, anchorCounts: Map<string, number>): string {
  const baseAnchor = `${sectionAnchor}-${getIdFromText(headingText)}`;
  const count = anchorCounts.get(baseAnchor) ?? 0;
  anchorCounts.set(baseAnchor, count + 1);

  if (count === 0) return baseAnchor;

  return `${baseAnchor}-${count + 1}`;
}
